<div class="my-2">
  <p-selectButton [options]="modes" [ngModel]="mode" (ngModelChange)="setMode($event)"
                  optionLabel="label" optionValue="value" [allowEmpty]="false"></p-selectButton>
</div>

<div *ngIf="mode === 'omics'" class="omics-convert">
  <p class="my-2">
    Prepare an AnnData file (<code>.h5ad</code>), a SpatialData store or a 10x Xenium bundle for the
    Spatial Omics plot mode. The server works in the background and saves the result next to the data.
    An AnnData dataset opens in Spatial Omics once it is converted; a dataset with transcripts can also
    get a transcript pyramid, so transcripts and gene selections are drawn at every zoom.
    Select the file, store or bundle in the 'Files' tab.
  </p>
  <p-divider align="left">
    <div class="inline-flex align-items-center font-bold">Omics Dataset</div>
  </p-divider>

  <div *ngIf="!omicsTarget" class="my-2 text-color-secondary" id="omics-nothing">Nothing is selected.</div>
  <ng-container *ngIf="omicsTarget">
    <div class="field grid py-0">
      <label class="col-12 md:col-4 text-sm">Selected:</label>
      <div class="col-12 md:col-8 text-sm" id="omics-target">{{ omicsTarget.name }}</div>
    </div>
    <div class="field grid py-0">
      <label class="col-12 md:col-4 text-sm">Storage Bucket:</label>
      <div class="col-12 md:col-8 text-sm">{{ bucket }}</div>
    </div>
    <div class="my-2 text-sm" id="omics-kind">
      <span *ngIf="omicsDetecting">Checking the dataset...</span>
      <span *ngIf="!omicsDetecting && omicsDetection && !canPrepareKind(omicsDetection.kind)">
        Not an omics dataset that needs preparing
        <span *ngIf="omicsDetection.spatial">({{ omicsDetection.kind }} opens directly)</span>.
        Select an <code>.h5ad</code> file, a SpatialData store or a 10x Xenium bundle.
      </span>
      <span *ngIf="!omicsDetecting && omicsDetection && canPrepareKind(omicsDetection.kind)">
        Detected: {{ omicsKindLabel(omicsDetection.kind) }}
      </span>
    </div>
  </ng-container>

  <ng-container *ngIf="hasOmicsOptions">
    <p-divider align="left">
      <div class="inline-flex align-items-center font-bold">Build</div>
    </p-divider>
    <div class="field-checkbox my-1">
      <p-checkbox inputId="omics-transcripts" [binary]="true"
                  [(ngModel)]="omicsOptions.transcripts"></p-checkbox>
      <label for="omics-transcripts" class="ml-2 text-sm">
        Transcript pyramid
        <i class="pi pi-info-circle px-1" tooltipPosition="top"
           pTooltip="All transcripts binned at every zoom (count, centroid and dominant cell per bin),
                     so 'All genes' can be shown zoomed out."></i>
        <span *ngIf="omicsStatus?.built?.transcripts" class="text-color-secondary">
          (built, {{ omicsStatus?.built?.levels }} levels)
        </span>
      </label>
    </div>
    <div class="field-checkbox my-1">
      <p-checkbox inputId="omics-genes" [binary]="true" [(ngModel)]="omicsOptions.geneLevels"
                  [disabled]="!omicsOptions.transcripts"></p-checkbox>
      <label for="omics-genes" class="ml-2 text-sm">
        Per-gene levels
        <i class="pi pi-info-circle px-1" tooltipPosition="top"
           pTooltip="The same bins per gene, so a gene selection is drawn at any zoom from small reads."></i>
        <span *ngIf="omicsStatus?.built?.geneLevels" class="text-color-secondary">(built)</span>
      </label>
    </div>
    <div *ngIf="hasMorphologyOption" class="field-checkbox my-1">
      <p-checkbox inputId="omics-morphology" [binary]="true"
                  [(ngModel)]="omicsOptions.morphology"></p-checkbox>
      <label for="omics-morphology" class="ml-2 text-sm">
        Morphology images
        <i class="pi pi-info-circle px-1" tooltipPosition="top"
           pTooltip="Extract the bundle's morphology OME-TIFFs next to it,
                     so the tissue image can be shown under the cells."></i>
        <span *ngIf="omicsStatus?.built?.morphology" class="text-color-secondary">(built)</span>
      </label>
    </div>
    <div *ngIf="hasMarkersOption" class="field-checkbox my-1">
      <p-checkbox inputId="omics-markers" [binary]="true"
                  [(ngModel)]="omicsOptions.markers"></p-checkbox>
      <label for="omics-markers" class="ml-2 text-sm">
        Marker genes
        <i class="pi pi-info-circle px-1" tooltipPosition="top"
           pTooltip="Score the marker genes of the bundle's clusterings now, in one pass over its
                     expression matrix, so 'Add marker genes' shows them at once instead of reading
                     the matrix first."></i>
        <span *ngIf="omicsStatus?.built?.markers" class="text-color-secondary">(built)</span>
      </label>
    </div>
    <div class="field grid h-3rem align-items-center my-1">
      <label for="omics-levels" class="col-12 md:col-5 mb-0 text-sm">
        Pyramid levels
        <i class="pi pi-info-circle px-1" tooltipPosition="top"
           pTooltip="Each level's bins are twice as wide as the one below, from 1.95 µm:
                     7 reaches 125 µm, 8 reaches 250 µm."></i>
      </label>
      <div class="col-12 md:col-7">
        <p-inputNumber inputId="omics-levels" [(ngModel)]="omicsOptions.levels" [min]="1" [max]="8"
                       (onBlur)="onOmicsLevelsBlur()"
                       [showButtons]="true" [disabled]="!omicsOptions.transcripts"
                       class="p-inputtext-sm"></p-inputNumber>
      </div>
    </div>
  </ng-container>

  <div *ngIf="omicsStatus" class="my-2 text-sm" id="omics-status">
    <i class="pi mr-2"
       [ngClass]="{ 'pi-check-circle': omicsStatus.state === 'ready',
                    'pi-spin pi-spinner': omicsBusy,
                    'pi-times-circle': omicsStatus.state === 'failed',
                    'pi-info-circle': omicsStatus.state === 'required' }"></i>
    <span *ngIf="omicsStatus.state === 'ready' && !canPrepareOmics">
      Already prepared: open it in Spatial Omics.
    </span>
    <span *ngIf="omicsStatus.state === 'ready' && canPrepareOmics">
      Ready to open; Run builds what is selected and missing.
    </span>
    <span *ngIf="omicsStatus.state === 'required'">Not prepared yet.</span>
    <span *ngIf="omicsStatus.state === 'submitting'">Submitting the convert job...</span>
    <span *ngIf="omicsStatus.state === 'running'">Preparing...</span>
    <span *ngIf="omicsStatus.state === 'failed'">Preparation failed: {{ omicsStatus.error }}</span>
    <div *ngIf="omicsStatus.workflowId" class="mt-1 text-color-secondary">
      Progress is also shown in the process status list.
    </div>
  </div>
  <div *ngIf="omicsError" class="my-2 text-sm p-error" id="omics-error">{{ omicsError }}</div>

  <div class="pt-3 pb-3 process-buttons flex align-items-center gap-3">
    <button pButton type="button" id="omics-run" label="Prepare for Spatial Omics" icon="pi pi-play"
            class="p-button-primary" [disabled]="!canPrepareOmics" (click)="onPrepareOmics()"></button>
  </div>
</div>

<form *ngIf="mode === 'bioformats'" [formGroup]="form" (ngSubmit)="onSubmit()">
		
	<p class="my-2">
	  Please select an image file or a folder containing one or more image files in
	  the 'Files' tab. These arguments are a subset of BFConvert
		(more may be added upon request).
	</p>
	<p-divider align="left">
	  <div class="inline-flex align-items-center font-bold">
	    BFConvert Arguments
	  </div>
	</p-divider>

	<!-- series -->
	<div class="field grid h-3rem align-items-center my-1">
		
		<label class="col-12 md:col-5 mb-0 text-black-alpha-90">
		    Series
		    <i class="pi pi-info-circle px-1"
		      pTooltip="[-series series] Series if image is a pyramid file, e.g. ndpi "
		      tooltipPosition="top"
		    ></i>
		</label>

		<div class="col-12 md:col-5">
		  <p-dropdown
		    id="series"
		    formControlName="level"
		    placeholder="Select Series"
		    [options]="levels"
		    optionLabel="shape"
		    optionValue="level"
		  ></p-dropdown>
		</div>

	</div>
	
	<p-accordion [multiple]="false" class="mt-3">
	  <p-accordionTab header="Advanced" [selected]="false">
	    
	    <div class="field grid h-3rem align-items-center my-1">
	      <label class="col-12 md:col-5 mb-0 text-black-alpha-90">
	        Compression
	        <i
	          class="pi pi-info-circle px-1"
	          pTooltip="[-compression codec] for BFConvert"
	          tooltipPosition="top"
	        ></i>
	      </label>
	      <div class="col-12 md:col-7">
	        <p-dropdown
	          id="compression"
	          formControlName="compression"
	          placeholder="Select compression (optional)"
	          [options]="compressionOptions"
	        ></p-dropdown>
	      </div>
	    </div>

	    <div class="field grid h-3rem align-items-center my-1">
	      <label class="col-12 md:col-5 mb-0 text-black-alpha-90">
	        Channel
	        <i
	          class="pi pi-info-circle px-1"
	          pTooltip="[-channel channel] The channel to use."
	          tooltipPosition="top"
	        ></i>
	      </label>
	      <div class="col-12 md:col-7">
	        <p-inputNumber
	          inputId="channel"
	          formControlName="channel"
	          [showButtons]="false"
	          [min]="0"
	          [max]="1000000"
	          class="p-inputtext-sm"
	        ></p-inputNumber>
	      </div>
	    </div>

	    <div class="field grid h-3rem align-items-center my-1">
	      <label class="col-12 md:col-5 mb-0 text-black-alpha-90">
	        Z Index
	        <i
	          class="pi pi-info-circle px-1"
	          pTooltip="[-z Z] Z stack index to convert"
	          tooltipPosition="top"
	        ></i>
	      </label>
	      <div class="col-12 md:col-7">
	        <p-inputNumber
	          inputId="z"
	          formControlName="z"
	          [showButtons]="false"
	          [min]="0"
	          [max]="1000000"
	          class="p-inputtext-sm"
	        ></p-inputNumber>
	      </div>
	    </div>

	    <div class="field grid h-3rem align-items-center my-1">
	      <label class="col-12 md:col-5 mb-0 text-black-alpha-90">
	        Timepoint
	        <i
	          class="pi pi-info-circle px-1"
	          pTooltip="[-timepoint timepoint] Timepoint BFConvert argument."
	          tooltipPosition="top"
	        ></i>
	      </label>
	      <div class="col-12 md:col-7">
	        <p-inputNumber
	          inputId="timepoint"
	          formControlName="timepoint"
	          [showButtons]="false"
	          [min]="0"
	          [max]="1000000"
	          class="p-inputtext-sm"
	        ></p-inputNumber>
	      </div>
	    </div>

	    <div class="field grid h-3rem align-items-center my-1">
	      <label class="col-12 md:col-5 mb-0 text-black-alpha-90">
	        Tile X
	        <i
	          class="pi pi-info-circle px-1"
	          pTooltip="[-tilex tileSizeX] Tile size in pixels, x-value."
	          tooltipPosition="top"
	        ></i>
	      </label>
	      <div class="col-12 md:col-7">
	        <p-inputNumber
	          inputId="tilex"
	          formControlName="tilex"
	          [showButtons]="false"
	          [min]="0"
	          [max]="1000000"
	          class="p-inputtext-sm"
	        ></p-inputNumber>
	      </div>
	    </div>

	    <div class="field grid h-3rem align-items-center my-1">
	      <label class="col-12 md:col-5 mb-0 text-black-alpha-90">
	        Tile Y
	        <i
	          class="pi pi-info-circle px-1"
	          pTooltip="[-tiley tileSizeY] Tile size in pixels, y-value."
	          tooltipPosition="top"
	        ></i>
	      </label>
	      <div class="col-12 md:col-7">
	        <p-inputNumber
	          inputId="tiley"
	          formControlName="tiley"
	          [showButtons]="false"
	          [min]="0"
	          [max]="1000000"
	          class="p-inputtext-sm"
	        ></p-inputNumber>
	      </div>
	    </div>

	  </p-accordionTab>
	</p-accordion>
	<br>
	
	<ng-container>
	   <p-divider align="left">
	     <div class="inline-flex align-items-center font-bold">
	       File Destination
	     </div>
	   </p-divider>
		 
		 <div class="field grid h-3rem align-items-center">
		   <label for="fnOutputName" class="col-12 md:col-5 mb-0 text-black-alpha-90">
		     Output Name
				 <i class="pi pi-info-circle px-1"
				    pTooltip="<p>The output name to the conversion tool. 
				 							Note: this is the bioformats convert tool 
				 							name so wildcards must be used. For  
				 							example if using tiling,the name must 
				 							use %x and %y expanded values to get 
				 							all the tiles created.</p>
				 						  Wildcards are:
				 	 <br/>
				 	 <table>
				        <tr><th>Wildcard</th><th>Description</th></tr>
				        <tr><td>%s</td><td>series index</td></tr>
				        <tr><td>%n</td><td>series name</td></tr>
				        <tr><td>%c</td><td>channel index</td></tr>
				        <tr><td>%w</td><td>channel name</td></tr>
				        <tr><td>%z</td><td>Z index</td></tr>
				        <tr><td>%t</td><td>T index</td></tr>
				        <tr><td>%A</td><td>acquisition timestamp</td></tr>
				        <tr><td>%x</td><td>row index of the tile</td></tr>
				        <tr><td>%y</td><td>column index of the tile</td></tr>
				        <tr><td>%m</td><td>overall tile index</td></tr>
				     </table>"
				    [escape]="false" tooltipPosition="top">
				 </i>
		   </label>
		   <div class="col-12 md:col-7">
		   <span *ngIf="!isOutputNameEditable">
		     {{form.controls['outputName'].value}}
		     <i class="pi pi-pencil icon-hover mx-2" (click)="onEditOutputName(true)"></i>
		   </span>
		     <span *ngIf="isOutputNameEditable" class="p-input-icon-right">
		     <i class="pi pi-times icon-hover" (click)="onEditOutputName(false)"></i>
		     <input id="fnOutputName" type="text" class="p-inputtext-sm w-full"
		            formControlName="outputName" pInputText/>
		   </span>
		   </div>
		 </div>

		 
		 <!-- storage bucket label -->
		 <div class="field grid py-0">
		   <label for="fn-bucket"
		          class="col-12 mb-2 md:col-4 md:mb-0 right-0 text-black-alpha-90 text-sm">
		     Storage Bucket:
		   </label>
		   <div class="col-12 md:col-8 text-sm">
		     <span id="fn-bucket">{{bucket}}</span>
		   </div>
		 </div>

	   <!-- output folder -->
		 <div class="field grid h-3rem align-items-center">
		   <label for="fnOutputFolder" class="col-12 md:col-5 mb-0 text-black-alpha-90">
		     Output Folder
				 <i class="pi pi-info-circle px-1"
				    pTooltip="The output path set can be a full path if leading 
						with '/' and ending with '/'. Otherwise, the results will be 
						saved in the folder name provided in the directory of the input
				    file(s) selected. If no file is selected, an output directory 
						with the same name as the selected file will be created and 
						the results saved in it."
				    tooltipPosition="top"
				 ></i>
		   </label>
		   <div class="col-12 md:col-7">
		   <span *ngIf="!isOutputFolderEditable">
		     {{form.controls['outputFolder'].value}}
		     <i class="pi pi-pencil icon-hover mx-2" (click)="onEditOutputFolder(true)"></i>
		   </span>
		     <span *ngIf="isOutputFolderEditable" class="p-input-icon-right">
		     <i class="pi pi-times icon-hover" (click)="onEditOutputFolder(false)"></i>
		     <input id="fnOutputFolder" type="text" class="p-inputtext-sm w-full"
		            formControlName="outputFolder" pInputText/>
		   </span>
		   </div>
		 </div>

		<div class="pt-3 pb-3 process-buttons flex align-items-center gap-3">
		  <app-submit-button
		    label="Run"
		    [isSubmitting]="isSubmitting"
				[processName]="processName"
		    [validationMessage]="validationMessage"
		    icon="pi pi-play"
		    styleClass="p-button-primary"
		    [tooltip]="'Run ' + processName"
		  ></app-submit-button>
		  <button pButton type="button" label="Create Rule" icon="pi pi-bell" class="p-button-outlined"
		          [disabled]="(canCreateRule$ | async) === false || form.invalid"
		          pTooltip="Auto-run this on every new file in the selected folder."
		          (click)="onCreateRule()"></button>
		</div>

	 </ng-container>
</form>